import React, { Component } from "react";
import { withRouter } from "./withRouter";
import { tableFromIPC } from "apache-arrow";
import {
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  TextField,
  MenuItem,
} from "@mui/material";

import { rowsFromTable } from "./utils/arrowUtils";
import { PARTY_COLOURS } from "./constants/partyConfig";
import { CONSTITUENCY_OPTIONS as possibleAreas } from "./constants/areas";


const POLICY_LABELS = {
  health_importance_p: "Fixing the Health System",
  maori_importance_p: "Managing Māori Issues",
  invest_importance_p: "Investing in Transport and Infrastructure",
  crime_importance_p: "Controlling Crime",
  col_importance_p: "Managing the Cost of Living",
  ed_importance_p: "Improving Education",
};

const POLICY_COL_LABEL = {
  health_importance_p: "health",
  maori_importance_p: "maori",
  invest_importance_p: "invest",
  crime_importance_p: "crime",
  col_importance_p: "col",
  ed_importance_p: "ed",
};

/* ── Delta display helper ─────────────────────────────────── */
const DeltaSpan = ({ delta, decimals = 0, suffix = "" }) => {
  if (delta == null || isNaN(delta)) return null;
  const sign = delta > 0 ? "+" : "";
  const color = delta > 0 ? "green" : delta < 0 ? "red" : "grey";
  return (
    <span style={{ color, fontSize: "0.85em" }}>
      {" "}
      ({sign}
      {delta.toFixed(decimals)}
      {suffix})
    </span>
  );
};

class PolicyView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      geoLevel: "constituency",
      metricRows: null,
      loading: false,
      hoverInfo: null,
      hoverAreaId: null,
      selectedStateBounds: null,
      selectedFeature: null,
      geoData: props.constituencyMap,
      styleLoaded: false,
      viewState: null,
      metricRowsConst: null,
      metricRowsNational: null,
      chosenDemog: "Gender",
      scatterDemogsRows: null,
      chosenGender: null,
      chosenAge: null,
      chosenEthnicity: null,
      chosenEducation: null,
      chosenPV: null,
      chosenElectorate: null,
      // wave-related state
      currentWave: null,
      previousWave: null,
      waveDate: null,
    };

    this.mapRef = React.createRef();
    this._inflight = null;
    this._lastKey = null;
    this._lastAt = 0;
  }

  componentDidMount() {
    const run = async () => {
      await this.fetchMetrics("national", null, true);
    };
    run();
  }

  componentDidUpdate(prevProps, prevState) {
    const chosenGenderChanged =
      prevState.chosenGender !== this.state.chosenGender;
    const chosenAgeChanged = prevState.chosenAge !== this.state.chosenAge;
    const chosenEthnicityChanged =
      prevState.chosenEthnicity !== this.state.chosenEthnicity;
    const chosenEducationChanged =
      prevState.chosenEducation !== this.state.chosenEducation;
    const chosenPVChanged = prevState.chosenPV !== this.state.chosenPV;
    const chosenElectorateChanged =
      prevState.chosenElectorate !== this.state.chosenElectorate;
    const filtersChanged =
      chosenGenderChanged ||
      chosenAgeChanged ||
      chosenEthnicityChanged ||
      chosenPVChanged ||
      chosenElectorateChanged ||
      chosenEducationChanged;

    if (filtersChanged) {
      const run = async () => {
        await this.fetchMetrics(
          "national",
          this.state.chosenElectorate,
          true
        );
      };
      run();
    }
  }

  /* ── Wave helpers ──────────────────────────────────────── */

  /**
   * Given an array of rows (one per wave), return the most-recent
   * row as `current` and the second-most-recent as `previous`.
   */
  deriveWaveData = (rows) => {
    if (!rows || rows.length === 0)
      return { current: null, previous: null };

    // Sort descending by wave number (highest = most recent)
    const sorted = [...rows].sort((a, b) => b.wave - a.wave);
    const current = sorted[0];
    const previous = sorted.length > 1 ? sorted[1] : null;

    return { current, previous };
  };

  /* ── Networking ────────────────────────────────────────── */

  requestKey = (level) => `${level}`;

  fetchMetrics = async (level, constCode, save_const_results) => {
    const controller = new AbortController();
    const signal = controller.signal;

    const reqKey = this.requestKey(level);
    const now = Date.now();
    if (this._lastKey === reqKey && now - this._lastAt < 700) return;
    this._lastKey = reqKey;
    this._lastAt = now;

    const keysBody = [
      "ed_importance_p",
      "health_importance_p",
      "col_importance_p",
      "crime_importance_p",
      "maori_importance_p",
      "invest_importance_p",
      "gov_well_ed_tot",
      "gov_poor_ed_tot",
      "gov_dontknow_ed",
      "gov_well_health_tot",
      "gov_poor_health_tot",
      "gov_dontknow_health",
      "gov_well_col_tot",
      "gov_poor_col_tot",
      "gov_dontknow_col",
      "gov_well_crime_tot",
      "gov_poor_crime_tot",
      "gov_dontknow_crime",
      "gov_well_maori_tot",
      "gov_poor_maori_tot",
      "gov_dontknow_maori",
      "gov_well_invest_tot",
      "gov_poor_invest_tot",
      "gov_dontknow_invest",
      "nat_p_col_poor",
      "nat_p_col_well",
      "act_p_col_poor",
      "act_p_col_well",
      "nzf_p_col_poor",
      "nzf_p_col_well",
      "nat_p_crime_poor",
      "nat_p_crime_well",
      "act_p_crime_poor",
      "act_p_crime_well",
      "nzf_p_crime_poor",
      "nzf_p_crime_well",
      "nat_p_ed_poor",
      "nat_p_ed_well",
      "act_p_ed_poor",
      "act_p_ed_well",
      "nzf_p_ed_poor",
      "nzf_p_ed_well",
      "nat_p_health_poor",
      "nat_p_health_well",
      "act_p_health_poor",
      "act_p_health_well",
      "nzf_p_health_poor",
      "nzf_p_health_well",
      "nat_p_invest_poor",
      "nat_p_invest_well",
      "act_p_invest_poor",
      "act_p_invest_well",
      "nzf_p_invest_poor",
      "nzf_p_invest_well",
      "nat_p_maori_poor",
      "nat_p_maori_well",
      "act_p_maori_poor",
      "act_p_maori_well",
      "nzf_p_maori_poor",
      "nzf_p_maori_well",
    ];

    let turnout_weight = "n_raked";

    const body = {
      groupKeys: keysBody,
      filters: {
        gender: this.state.chosenGender ?? null,
        age: this.state.chosenAge ?? null,
        ethnicity: this.state.chosenEthnicity ?? null,
        education: this.state.chosenEducation ?? null,
        vote_2023: this.state.chosenPV ?? null,
      },
      constCode: constCode,
      turnout_weight: turnout_weight,
    };

    this.setState({ loading: true });

    try {
      const resMain = await fetch(`/api/policy_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      });
      if (!resMain.ok) throw new Error(`metrics fetch ${resMain.status}`);
      const bufMain = await resMain.arrayBuffer();
      const tableMain = tableFromIPC(bufMain);
      const rowsMain = rowsFromTable(tableMain);

      // Derive current + previous wave from all returned rows
      const { current, previous } = this.deriveWaveData(rowsMain);

      this.setState({
        metricRows: rowsMain,
        currentWave: current,
        previousWave: previous,
        waveDate: current?.wave_date ?? null,
        loading: false,
      });
    } catch (e) {
      if (e.name !== "AbortError") console.error("metrics error:", e);
      this.setState({
        metricRows: null,
        currentWave: null,
        previousWave: null,
        waveDate: null,
        loading: false,
      });
    } finally {
      this._inflight = null;
    }
  };

  /* ── Filter handlers ───────────────────────────────────── */

  handlechosenGenderChange = (val) => this.setState({ chosenGender: val });
  handlechosenAgeChange = (val) => this.setState({ chosenAge: val });
  handlechosenEthnicityChange = (val) =>
    this.setState({ chosenEthnicity: val });
  handlechosenEducationChange = (val) =>
    this.setState({ chosenEducation: val });
  handlechosenPVChange = (val) => this.setState({ chosenPV: val });
  handlechosenElectorateChange = (val) =>
    this.setState({ chosenElectorate: val });

  /* ── Render ────────────────────────────────────────────── */

  render() {
    const { currentWave, previousWave, waveDate } = this.state;

    let policies;

    if (currentWave) {
      const row = currentWave;
      const prev = previousWave;

      policies = Object.keys(POLICY_LABELS)
        .map((k) => {
          const col = POLICY_COL_LABEL[k];
          const importance = row[`${col}_importance_p`] ?? 0;
          const poor = row[`gov_poor_${col}_tot`] ?? 0;
          const well = row[`gov_well_${col}_tot`] ?? 0;

          const prevImportance = prev?.[`${col}_importance_p`] ?? null;
          const prevPoor = prev?.[`gov_poor_${col}_tot`] ?? null;
          const prevWell = prev?.[`gov_well_${col}_tot`] ?? null;

          return {
            key: k,
            label: POLICY_LABELS[k],
            importance,
            poor,
            well,
            importanceDelta:
              prevImportance != null ? importance - prevImportance : null,
            poorDelta: prevPoor != null ? poor - prevPoor : null,
            wellDelta: prevWell != null ? well - prevWell : null,
          };
        })
        .sort((a, b) => a.importance - b.importance);
    }

    /* Build blame / credit columns with deltas */
    const buildPartyColumns = (row, policyKey, prevRow) => {
      const parties = ["nat", "act", "nzf"];
      const col = POLICY_COL_LABEL[policyKey];

      const poor = parties.map((p) => {
        const val = row[`${p}_p_${col}_poor`];
        const prevVal = prevRow?.[`${p}_p_${col}_poor`] ?? null;
        return {
          party: p.toUpperCase(),
          val,
          prevVal,
          color: PARTY_COLOURS[p],
        };
      });

      const well = parties.map((p) => {
        const val = row[`${p}_p_${col}_well`];
        const prevVal = prevRow?.[`${p}_p_${col}_well`] ?? null;
        return {
          party: p.toUpperCase(),
          val,
          prevVal,
          color: PARTY_COLOURS[p],
        };
      });

      // Normalise current values
      const poorSum = poor.reduce((a, c) => a + c.val, 0);
      const wellSum = well.reduce((a, c) => a + c.val, 0);

      // Normalise previous values (for deltas)
      const prevPoorSum = poor.reduce((a, c) => a + (c.prevVal ?? 0), 0);
      const prevWellSum = well.reduce((a, c) => a + (c.prevVal ?? 0), 0);

      poor.forEach((e) => {
        const normCurr = poorSum ? (e.val / poorSum) * 100 : 0;
        const normPrev =
          prevPoorSum && e.prevVal != null
            ? (e.prevVal / prevPoorSum) * 100
            : null;
        e.delta = normPrev != null ? normCurr - normPrev : null;
        e.val = normCurr;
      });

      well.forEach((e) => {
        const normCurr = wellSum ? (e.val / wellSum) * 100 : 0;
        const normPrev =
          prevWellSum && e.prevVal != null
            ? (e.prevVal / prevWellSum) * 100
            : null;
        e.delta = normPrev != null ? normCurr - normPrev : null;
        e.val = normCurr;
      });

      poor.sort((a, b) => b.val - a.val);
      well.sort((a, b) => b.val - a.val);

      return { poor, well };
    };

    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {/* HEADER */}
        <Box
          sx={(theme) => ({
            height: "20%",
            px: 3,
            py: 3,
            display: "flex",
            alignItems: "center",
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ ml: 4 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Policy Scorecard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Select a demographic to view their policy-party scores<br/>
                {waveDate && ` — Wave: ${waveDate}`} (Change from previous wave)
              </Typography>
            </Box>

            <TextField
              select
              label="Gender"
              size="small"
              value={this.state.chosenGender}
              onChange={(e) =>
                this.handlechosenGenderChange?.(e.target.value)
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value="M">Male</MenuItem>
              <MenuItem value="F">Female</MenuItem>
            </TextField>

            <TextField
              select
              label="Age"
              size="small"
              value={this.state.chosenAge}
              onChange={(e) =>
                this.handlechosenAgeChange?.(e.target.value)
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value="Young">Young</MenuItem>
              <MenuItem value="Middle">Middle</MenuItem>
              <MenuItem value="Old">Old</MenuItem>
            </TextField>

            <TextField
              select
              label="Education"
              size="small"
              value={this.state.chosenEducation}
              onChange={(e) =>
                this.handlechosenEducationChange?.(e.target.value)
              }
              sx={{ mt: 3, minWidth: 180 }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Mid">Mid</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </TextField>

            <TextField
              select
              label="Ethnicity"
              size="small"
              value={this.state.chosenEthnicity}
              onChange={(e) =>
                this.handlechosenEthnicityChange?.(e.target.value)
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value="Asian only">Asian only</MenuItem>
              <MenuItem value="European only">European only</MenuItem>
              <MenuItem value="European/Māori">European/Māori</MenuItem>
              <MenuItem value="Māori only">Māori only</MenuItem>
              <MenuItem value="Pacific Peoples only">Other</MenuItem>
            </TextField>

            {/* <TextField
              select
              label="2023 Vote"
              size="small"
              value={this.state.chosenPV}
              onChange={(e) =>
                this.handlechosenPVChange?.(e.target.value)
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value="ACT">ACT 2023</MenuItem>
              <MenuItem value="GREEN">Green 2023</MenuItem>
              <MenuItem value="LAB">Labour 2023</MenuItem>
              <MenuItem value="NAT">National 2023</MenuItem>
              <MenuItem value="NZF">NZF 2023</MenuItem>
              <MenuItem value="TPM">TPM 2023</MenuItem>
              <MenuItem value="OTH">Other 2023</MenuItem>
              <MenuItem value="DNV">Did not vote</MenuItem>
            </TextField> */}

            <TextField
              select
              label="Electorate"
              size="small"
              value={this.state.chosenElectorate}
              onChange={(e) =>
                this.handlechosenElectorateChange?.(e.target.value)
              }
              sx={{ minWidth: 180 }}
            >
              {possibleAreas.map((area) => (
                <MenuItem
                  key={area.area_name ?? "all"}
                  value={area.area_id}
                >
                  {area.area_name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        {/* CARDS GRID */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(6, 1fr)",
            },
            gap: 2,
            width: "100%",
            height: "100%",
          }}
        >
          {policies &&
            policies.map((p, i) => {
              const total = p.poor + p.well;
              const poorPct = total > 0 ? (p.poor / total) * 100 : 0;
              const wellPct = total > 0 ? (p.well / total) * 100 : 0;
              const netWell = Math.round(p.well - p.poor);

              // Previous-wave net well for delta
              const prevNetWell =
                p.wellDelta != null && p.poorDelta != null
                  ? Math.round(p.wellDelta - p.poorDelta)
                  : null;

              // Build party columns with deltas
              const partyCols = buildPartyColumns(
                currentWave,
                p.key,
                previousWave
              );

              return (
                <Card
                  key={p.key}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      height: "100%",
                    }}
                  >
                    {/* Top block */}
                    <Box
                      sx={{
                        minHeight: 180,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        textAlign: "center",
                        px: 1,
                      }}
                    >
                      <Typography variant="h4" fontWeight={700}>
                        {i + 1}
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {p.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Average Importance Ranking:{" "}
                        {(p.importance / 100).toFixed(0)}
                        <DeltaSpan
                          delta={
                            p.importanceDelta != null
                              ? p.importanceDelta / 100
                              : null
                          }
                          decimals={1}
                        />
                      </Typography>
                    </Box>

                    {/* Net Well bar */}
                    <Box
                      sx={{
                        width: "100%",
                        mt: 2,
                        mb: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, textAlign: "center", fontSize: 15 }}
                      >
                        <b>Net 'Well'</b> :{" "}
                        {netWell > 0 ? `+${netWell}` : netWell}%
                        <DeltaSpan delta={prevNetWell} />
                      </Typography>

                      <Box
                        sx={{ display: "flex", width: "100%", height: 20 }}
                      >
                        <Box
                          sx={{ width: `${wellPct}%`, bgcolor: "lightgreen" }}
                        />
                        <Box
                          sx={{ width: `${poorPct}%`, bgcolor: "tomato" }}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 0.5,
                          fontSize: "1rem",
                        }}
                      >
                        <span>
                          {p.well.toFixed(0)}%
                          <DeltaSpan delta={p.wellDelta} />
                        </span>
                        <span>
                          {p.poor.toFixed(0)}%
                          <DeltaSpan delta={p.poorDelta} />
                        </span>
                      </Box>
                    </Box>

                    {/* Bottom: party blame / credit */}
                    <Box sx={{ mt: "auto", width: "100%" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", mb: 1 }}
                      >
                        Which party is responsible for this policy going:
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1,
                        }}
                      >
                        {/* Well column */}
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ fontStyle: "italic" }}
                          >
                            Well
                          </Typography>
                          {partyCols.well.map((row) => (
                            <Box
                              key={row.party}
                              sx={{
                                bgcolor: row.color,
                                color:
                                  row.party === "ACT" ? "#818589" : "white",
                                p: 1,
                                textAlign: "center",
                                fontWeight: "bold",
                                mb: 0.5,
                              }}
                            >
                              {row.party} {row.val.toFixed(0)}%
                              <DeltaSpan delta={row.delta} />
                            </Box>
                          ))}
                        </Box>

                        {/* Poor column */}
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ fontStyle: "italic" }}
                          >
                            Poorly
                          </Typography>
                          {partyCols.poor.map((row) => (
                            <Box
                              key={row.party}
                              sx={{
                                bgcolor: row.color,
                                color:
                                  row.party === "ACT" ? "#818589" : "white",
                                p: 1,
                                textAlign: "center",
                                fontWeight: "bold",
                                mb: 0.5,
                              }}
                            >
                              {row.party} {row.val.toFixed(0)}%
                              <DeltaSpan delta={row.delta} />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
        </Box>
      </Box>
    );
  }
}

export default withRouter(PolicyView);